import { Component, Input, OnInit, ViewChild, ElementRef } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { ToastrService } from "ngx-toastr";
import { Observable, of, firstValueFrom } from "rxjs";
import { catchError, tap } from "rxjs/operators";

import { PdfToPngService } from "src/app/digitization-tool/services/pdf-to-png/pdf-to-png.service";
import { OrderDetailStore } from "src/app/order-detail/+state/order-detail.store";
import { FormError } from "src/app/shared/components/app-form-error/form-error";
import { Features } from "src/app/shared/enums/Features";
import { AccountSignatureModel } from "src/app/shared/models/account-signature.model";
import {
  Commission,
  CommissionPatchSelectedSignature,
} from "src/app/shared/models/commission.model";
import { CommissionInfoPatch } from "src/app/shared/models/commission-info.model";
import { StateRuleModel } from "src/app/shared/models/state-rules.model";
import { AppSpinnerModule } from "src/app/shared/modules/app-spinner.module";
import { AccountService } from "src/app/shared/services/account.service";
import { AccountSignatureService } from "src/app/shared/services/account-signature.service";
import { CommissionService } from "src/app/shared/services/commission.service";
import {
  base64ToBlob,
  convertArrayBufferToBase64,
  DataMatrixScanResult,
} from "src/app/shared/services/data-matrix/utils";
import { DataMatrixService } from "src/app/shared/services/data-matrix/data-matrix.service";
import { FeaturesService } from "src/app/shared/services/features.service";
import { SignatureProcessingService } from "src/app/shared/services/signature-processing.service";
import { FormValidators } from "src/app/shared/validators/form.validators";

@UntilDestroy()
@Component({
  selector: "app-signature-upload",
  templateUrl: "./signature-upload.component.html",
  styleUrls: ["./signature-upload.component.scss"],
  standalone: false,
})
export class SignatureUploadComponent implements OnInit {
  @Input() currentAccountId: number;
  @Input() isReadDataFromSassEnabled = false;
  @Input() mode: "add" | "edit";

  // Only relevant when updating signature from a commission
  @Input() commission: any;
  @Input() selectedRule: Observable<StateRuleModel>;
  @Input() signatures: AccountSignatureModel[] = [];

  accountSignatureSelected: Observable<boolean> = of(false);
  hasSignature: boolean;
  isProcessing = false;
  isSignatureSelectedFromFormField = false;
  selectedAccountSignature: AccountSignatureModel | null;
  selectedSignatureBase64: string | null;
  selectedFileName: string;
  signatureUploaded: Observable<boolean>;
  uploadSignatureForm: UntypedFormGroup;

  // A part of Signature Detection Intelligence feature
  isSignatureValid: any = undefined;
  isSignatureDetectionLoading = false;
  isSignatureDetectionIntelligenceEnabledStream$: Observable<boolean>;
  isSignatureDetectionIntelligenceEnabled = false;

  // A part of Automate Digitization Of Handdrawn Signature feature
  isAutomateDigitizationOfHanddrawnSignatureEnabledStream$: Observable<boolean>;
  isAutomateDigitizationOfHanddrawnSignatureEnabled = false;
  hasDataMatrix: boolean | undefined = undefined;

  // File input reference for triggering file selection
  @ViewChild("fileInput") fileInput!: ElementRef<HTMLInputElement>;

  constructor(
    private readonly accountService: AccountService,
    private readonly accountSignatureService: AccountSignatureService,
    private readonly activeModal: NgbActiveModal,
    private readonly commissionService: CommissionService,
    private readonly sanitizer: DomSanitizer,
    private readonly spinner: AppSpinnerModule,
    private readonly featureService: FeaturesService,
    private readonly toastr: ToastrService,
    private readonly store: OrderDetailStore,
    private readonly pdfToPngService: PdfToPngService,
    private readonly dataMatrixService: DataMatrixService,
    private readonly signatureProcessingService: SignatureProcessingService
  ) {}

  ngOnInit(): void {
    this.setFeatures();
    this.getIsSignatureDetectionIntelligenceEnabled();
    this.getIsAutomateDigitizationOfHanddrawnSignatureEnabled();
    this.initForm();

    if (!this.currentAccountId) {
      this.getCurrentAccount();
    }

    if (this.mode === "edit" && this.commission?.signature !== null) {
      this.selectedSignatureBase64 = this.commission.signature;
      this.hasSignature = true;
    }

    this.dataMatrixService.initializeZxing();
  }

  initForm() {
    const allowedFileTypes = this
      .isAutomateDigitizationOfHanddrawnSignatureEnabled
      ? ["png", "jpg", "jpeg", "pdf"]
      : ["png"];

    this.uploadSignatureForm = new UntypedFormGroup(
      {
        signature: new UntypedFormControl(
          null,
          FormValidators.requiredFileType(allowedFileTypes)
        ),
        name: new UntypedFormControl(""),
      },
      { updateOn: "change" }
    );

    if (!!this.commission) {
      const accountSignature = new UntypedFormControl([]);
      accountSignature?.valueChanges.subscribe(
        (signature: AccountSignatureModel) => {
          this.isSignatureSelectedFromFormField =
            !!signature?.accountSignatureId;
          this.isSignatureValid = undefined;
          this.selectAccountSignature(signature);
        }
      );
      this.uploadSignatureForm.addControl("accountSignature", accountSignature);
    }
  }

  selectAccountSignature(signature: AccountSignatureModel) {
    if (!signature || !signature.accountSignatureId) {
      return;
    }
    const selectedSignature = this.signatures.find((s) =>
      this.isReadDataFromSassEnabled
        ? s.signatureGuid === signature.signatureGuid
        : s.accountSignatureId === signature.accountSignatureId
    );
    this.selectedAccountSignature =
      selectedSignature || this.selectedAccountSignature;
    this.selectedSignatureBase64 =
      selectedSignature?.signatureImageBase64 || null;
    this.accountSignatureSelected = of(true);
    this.hasSignature = true;
  }

  async onFileSelected(event: Event): Promise<void> {
    const target = event.target as HTMLInputElement;
    if (target.files === null || target.files.length === 0) {
      return;
    }
    const selectedSignatureFile = target.files[0];

    if (!this.validateFile(selectedSignatureFile)) {
      target.value = "";
      return;
    }

    this.isProcessing = true;
    this.spinner.show();

    await this.loadFileAsync(selectedSignatureFile);

    target.value = "";
    this.spinner.hide();
    this.isProcessing = false;
  }

  private async loadFileAsync(file: File): Promise<void> {
    this.hasSignature = false;
    this.hasDataMatrix = false;

    return new Promise<void>((resolve, reject) => {
      const reader = new FileReader();
      reader.addEventListener("load", async () => {
        await this.handleLoadFileAsync(reader, file, resolve, reject);
      });
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   *
   * @param file - the uploaded file
   * @param selectedSignatureBase64 - the base64 representation of the selected signature
   * @returns Promise<string>
   */
  private async extractPaper(
    file: File,
    selectedSignatureBase64: string
  ): Promise<string> {
    let signatureBase64 = "";

    try {
      if (!globalThis.cv) {
        // opencv is required for jscanify, and later during signature processing
        const { default: cvReadyPromise } = await import(
          "@techstark/opencv-js"
        );
        // jscanify expects cv to be globally available
        (globalThis as any).cv = await cvReadyPromise;
      }

      // use jscanify to isolate the document from the image
      const { default: jscanify } = await import("jscanify/src/jscanify.js");
      const scanner = new jscanify();

      // setup image for jscanify
      const image = new Image();
      image.src = `data:${file.type};base64,${selectedSignatureBase64}`;
      await new Promise<void>((onloadResolve, onloadReject) => {
        image.onload = () => onloadResolve();
        image.onerror = (err) => onloadReject(err);
      });

      const result = scanner.extractPaper(image, 2550, 3300);
      const base64 = result.toDataURL().split(",")[1];

      if (base64.length > 0 && typeof base64 === "string") {
        signatureBase64 = base64;
      } else {
        throw new Error("jscanify returned empty base64 data.");
      }
    } catch (e) {
      console.warn("jscanify processing failed, using original image.", e);
      signatureBase64 = selectedSignatureBase64;
    }

    return signatureBase64;
  }

  /**
   *
   * @param file - the uploaded file
   * @param selectedSignatureBase64 - the base64 representation of the selected signature
   * @param isPdf - whether the uploaded file is a PDF
   * @returns Promise<DataMatrixScanResult>
   */
  async scanForDataMatrix(
    fileType: string,
    selectedSignatureBase64: string,
    isPdf: boolean
  ): Promise<DataMatrixScanResult> {
    // scan for Data Matrix in the uploaded image
    this.resetDataMatrixChecks();

    if (isPdf) {
      // convert base64 png data to blob for scanForDataMatrix
      const pngBlob = base64ToBlob(selectedSignatureBase64, "image/png");

      // convert blob to file for scanForDataMatrix compatibility
      const pngFile = new File([pngBlob], "converted-signature.png", {
        type: "image/png",
      });

      return await this.dataMatrixService.scanForDataMatrix(pngFile);
    }

    return await this.dataMatrixService.scanForDataMatrix(
      new File([base64ToBlob(selectedSignatureBase64, fileType)], "signature", {
        type: fileType,
      })
    );
  }

  /**
   * handles the FileReader load event for processing uploaded signature files
   * @param reader - The FileReader instance
   * @param file - The uploaded file
   * @param resolve - Promise resolve function
   * @param reject - Promise reject function
   */
  private async handleLoadFileAsync(
    reader: FileReader,
    file: File,
    resolve: (value: void | PromiseLike<void>) => void,
    reject: (reason?: any) => void
  ): Promise<void> {
    try {
      this.selectedAccountSignature = null;

      const isPdf = file.type === "application/pdf";
      if (isPdf && this.isAutomateDigitizationOfHanddrawnSignatureEnabled) {
        const pngBase64 = await this.pdfToPngService.getPngFromPdfjs(
          reader.result as ArrayBuffer,
          10
        );

        this.selectedSignatureBase64 = pngBase64.split(",")[1];
      } else {
        this.selectedSignatureBase64 = convertArrayBufferToBase64(
          reader.result as ArrayBuffer
        );
      }

      if (this.isAutomateDigitizationOfHanddrawnSignatureEnabled) {
        const originalImage = this.selectedSignatureBase64;

        // Use JScanify to extract document from an image
        const extractedDocument = await this.extractPaper(file, originalImage);

        // Scan for data matrix in extracted image
        let dataMatrixScanResult = await this.scanForDataMatrix(
          "image/png", // the extractedDocument is always an image/png string
          extractedDocument,
          false
        );

        // If no Data Matrix was found in the extracted image, scan the original image
        if (!dataMatrixScanResult.hasDataMatrix) {
          dataMatrixScanResult = await this.scanForDataMatrix(
            file.type,
            originalImage,
            isPdf
          );
        }

        this.hasDataMatrix = dataMatrixScanResult.hasDataMatrix;
        if (dataMatrixScanResult.hasDataMatrix) {
          this.selectedSignatureBase64 = dataMatrixScanResult.base64Data;
        }

        if (this.hasDataMatrix) {
          await this.postProcessExtractedSignature();
        }
      }

      // use a large language model to validate the signature
      if (
        this.isSignatureDetectionIntelligenceEnabled &&
        this.isDataMatrixCheckPassed
      ) {
        this.isSignatureValid = undefined;
        this.isSignatureDetectionLoading = true;

        this.accountSignatureService
          .postIsValidateSignature(this.selectedSignatureBase64)
          .pipe(
            untilDestroyed(this),
            tap((response) => {
              this.isSignatureValid = response.isSignature;
              this.isSignatureDetectionLoading = false;
            }),
            catchError(() => {
              this.isSignatureValid = false;
              this.isSignatureDetectionLoading = false;
              return [];
            })
          )
          .subscribe();
      }

      // set the file name without the extension
      this.selectedFileName = file.name.substring(
        0,
        file.name.lastIndexOf(".")
      );

      this.signatureUploaded = new Observable<boolean>((observer) => {
        observer.next(
          !this.uploadSignatureForm
            .get("signature")
            ?.hasError("requiredFileType")
        );
        observer.complete();
      });
      resolve();
    } catch (err) {
      reject(err);
    }
  }

  getSanitizedImage(image: string): SafeUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(
      "data:image/png;base64," + image
    );
  }

  validateForm(): boolean {
    FormError.ValidateAllFormFields(this.uploadSignatureForm);
    if (!this.uploadSignatureForm.valid) {
      this.markFormControlsAsDirty(this.uploadSignatureForm);
      this.toastr.warning("Please correct all errors.", "Attention");
      this.spinner.hide();
      return false;
    }
    return true;
  }

  validateSignatureBase64(): boolean {
    if (this.selectedSignatureBase64 === null) {
      this.markFormControlsAsDirty(this.uploadSignatureForm);
      this.toastr.warning("Please correct all errors.", "Attention");
      this.spinner.hide();
      return false;
    }
    return true;
  }

  async saveSignature() {
    if (!!this.commission && this.validateSignatureBase64()) {
      // Create Account Signature if a new signature was uploaded
      if (!this.selectedAccountSignature) {
        await this.saveAccountSignature((result) => {
          if (!result.accountSignature) {
            return;
          }
          this.selectedAccountSignature = result.accountSignature;
          this.signatures.push(this.selectedAccountSignature);
          this.uploadSignatureForm.patchValue({
            accountSignature: this.selectedAccountSignature,
          });
        });
      }

      await this.saveSignatureToCommission();
    } else if (this.validateForm()) {
      await this.saveAccountSignature((_) => {
        this.closeOnSuccess("accountSignatureAdded");
      });
    }
  }

  async saveAccountSignature(
    callback: (result: AccountSignatureModel) => void
  ): Promise<void> {
    this.spinner.show();
    const value = this.uploadSignatureForm.getRawValue();

    // get current date time to append to the signature name
    const now = new Date();
    const formattedDateTime = `${now.getFullYear()}_${(now.getMonth() + 1)
      .toString()
      .padStart(2, "0")}_${now.getDate().toString().padStart(2, "0")}_${now
      .getHours()
      .toString()
      .padStart(2, "0")}${now.getMinutes().toString().padStart(2, "0")}`;
    const signatureName = `${this.selectedFileName}-${formattedDateTime}`;

    const signatureModel: AccountSignatureModel = {
      name: !!value.name ? value.name : signatureName,
      accountId: this.currentAccountId,
      signatureImageBase64: this.selectedSignatureBase64 ?? "",
      accountSignatureId: 0,
      filePathId: 0,
    };

    await firstValueFrom(
      this.accountSignatureService.saveSignature(
        this.isReadDataFromSassEnabled,
        signatureModel
      )
    )
      .then(callback)
      .catch(() => {
        this.spinner.hide();
        this.toastr.error("Failed to add Signature");
      });
  }

  async saveSignatureToCommission() {
    if (!this.validateForm()) return;

    this.spinner.show();

    if (!this.selectedAccountSignature) {
      this.toastr.warning(
        "Please select an existing signature or upload a new one.",
        "Attention"
      );
      this.spinner.hide();
      return;
    }

    if (this.isReadDataFromSassEnabled) {
      const sassCommissionPatch: CommissionPatchSelectedSignature = {
        commissionGuid: this.commission.sassGuid,
        signatureGuid: this.selectedAccountSignature.signatureGuid ?? "",
      };

      this.commissionService
        .updateSelectedSignature(sassCommissionPatch)
        .pipe(
          tap(() => {
            this.closeOnSuccess("signatureAdded");
          }),
          catchError(() => {
            this.spinner.hide();
            this.toastr.error("Failed saving signature");
            return [];
          })
        )
        .subscribe();
    } else {
      // If this is an active commission, we need to build the Commission model and patch it.
      // There's not an easy way to check this without refactoring the models to include more info, so we'll just check for the presence of the 'id' property.
      if (this.commission.id) {
        const commissionPatch: Commission = {
          id: this.commission.id,
          accountId: this.currentAccountId,
          countyFIPSCode: this.commission.countyFIPSCode,
          expiresOn: this.commission.expiresOn,
          name: this.commission.name,
          number: this.commission.number,
          stamp: this.commission.stamp,
          stateCode: this.commission.stateCode,
          signature: "",
          accountSignatureId: this.selectedAccountSignature.accountSignatureId,
          signatureFilePathId: this.selectedAccountSignature.filePathId,
          certificate: "",
        };
        this.commissionService
          .updateCommission(false, commissionPatch)
          .pipe(
            tap(() => {
              this.closeOnSuccess("signatureAdded");
            }),
            catchError(() => {
              this.spinner.hide();
              this.toastr.error("Failed saving signature");
              return [];
            })
          )
          .subscribe();
      }

      // If this is a draft commission, we need to build the CommissionInfo model and patch it
      if (this.commission.commissionInfoId) {
        const commissionInfoPatch: CommissionInfoPatch = {
          commissionInfoId: this.commission.commissionInfoId,
          accountSignatureId: this.selectedAccountSignature.accountSignatureId,
          expiresOn: this.commission.expiresOn,
          accountId: this.commission.accountId,
          stateCode: this.commission.stateCode,
          number: this.commission.number,
          stamp: this.commission.stamp,
          countyFipsCode: this.commission.countyFipsCode,
          name: this.commission.name,
        };
        this.commissionService
          .patchDraftCommission(commissionInfoPatch)
          .pipe(
            tap(() => {
              this.closeOnSuccess("signatureAdded");
            }),
            catchError(() => {
              this.spinner.hide();
              this.toastr.error("Failed to add Signature");
              return [];
            })
          )
          .subscribe();
      }
    }
  }

  getCurrentAccount(): void {
    this.accountService
      .getCurrentAccount()
      .pipe(
        tap((account) => {
          this.currentAccountId = account.accountId;
        })
      )
      .subscribe();
  }

  close(): void {
    this.activeModal.close();
  }

  closeOnSuccess(event: string): void {
    this.activeModal.close(event);
    this.spinner.hide();
    this.toastr.success("Signature Added");
    this.uploadSignatureForm.reset();
  }

  private markFormControlsAsDirty(formGroup: UntypedFormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      if (control instanceof UntypedFormGroup) {
        this.markFormControlsAsDirty(control);
      } else {
        control.markAsDirty();
      }
    });
  }

  setFeatures(): void {
    this.featureService
      .getFeatures()
      .pipe(
        tap(async (result) => {
          this.store.setFeatures(result);
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  getIsSignatureDetectionIntelligenceEnabled(): void {
    this.isSignatureDetectionIntelligenceEnabledStream$ =
      this.featureService.isFeatureEnabled(
        Features.SignatureDetectionIntelligence
      );

    this.isSignatureDetectionIntelligenceEnabledStream$
      .pipe(untilDestroyed(this))
      .subscribe((isEnabled) => {
        this.isSignatureDetectionIntelligenceEnabled = isEnabled;
      });
  }

  getIsAutomateDigitizationOfHanddrawnSignatureEnabled(): void {
    this.isAutomateDigitizationOfHanddrawnSignatureEnabledStream$ =
      this.featureService.isFeatureEnabled(
        Features.AutomateDigitizationOfHanddrawnSignature
      );

    this.isAutomateDigitizationOfHanddrawnSignatureEnabledStream$
      .pipe(untilDestroyed(this))
      .subscribe((isEnabled) => {
        this.isAutomateDigitizationOfHanddrawnSignatureEnabled = isEnabled;
        this.initForm();
      });
  }

  get allowedFileTypesMessage(): string {
    return this.isAutomateDigitizationOfHanddrawnSignatureEnabled
      ? "Please select a .PNG .JPG .JPEG, or .PDF file"
      : "Please select a .PNG file";
  }

  get supportedFormatsText(): string {
    return this.isAutomateDigitizationOfHanddrawnSignatureEnabled
      ? "PNG, JPG, JPEG, or PDF"
      : "PNG";
  }

  get maxFileSizeBytes(): number {
    return this.isAutomateDigitizationOfHanddrawnSignatureEnabled
      ? 20971520 // 20MB
      : 21504; // 21KB
  }

  get maxFileSizeText(): string {
    return this.isAutomateDigitizationOfHanddrawnSignatureEnabled
      ? "20MB"
      : "21KB";
  }

  async postProcessExtractedSignature(): Promise<void> {
    const processedImage =
      await this.signatureProcessingService.formatSignatureImage(
        this.selectedSignatureBase64
      );
    if (processedImage) {
      this.selectedSignatureBase64 = processedImage;
    }
  }

  // New methods for the updated UI
  triggerFileInput(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.fileInput.nativeElement.click();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  async onDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      await this.processDroppedFile(file);
    }
  }

  private validateFile(file: File): boolean {
    // Validate file size
    if (file.size > this.maxFileSizeBytes) {
      this.toastr.warning(
        `The file '${file.name}' is too large. Please select a file ${this.maxFileSizeText} or smaller.`,
        "Warning"
      );
      return false;
    }

    // Validate file type
    const allowedTypes = this.isAutomateDigitizationOfHanddrawnSignatureEnabled
      ? ["image/png", "image/jpeg", "image/jpg", "application/pdf"]
      : ["image/png"];

    if (!allowedTypes.includes(file.type)) {
      this.toastr.warning(
        `The file '${file.name}' is not a supported format. Please select a ${this.supportedFormatsText} file.`,
        "Warning"
      );
      return false;
    }

    return true;
  }

  private async processDroppedFile(file: File): Promise<void> {
    if (!this.validateFile(file)) {
      return;
    }

    // Update form control with the file
    this.uploadSignatureForm.patchValue({ signature: file });

    this.isProcessing = true;
    this.spinner.show();
    await this.loadFileAsync(file);
    this.spinner.hide();
    this.isProcessing = false;
  }

  getAcceptedFileTypes(): string {
    return this.isAutomateDigitizationOfHanddrawnSignatureEnabled
      ? ".png,.jpg,.jpeg,.pdf"
      : ".png";
  }

  getSampleSignature(): string {
    // Use the existing signature asset for examples
    return "assets/images/signature.png";
  }

  get isDataMatrixCheckPassed(): boolean | undefined {
    return (
      !this.isAutomateDigitizationOfHanddrawnSignatureEnabled ||
      this.hasDataMatrix
    );
  }

  private resetDataMatrixChecks(): void {
    this.hasDataMatrix = undefined;
    if (this.isSignatureDetectionIntelligenceEnabled) {
      this.isSignatureValid = undefined;
    }
  }
}
